import type { IndividualPaymentInvoiceViewModel } from '@/shared/api/generated';
import { useGetApiV1IndividualpaymentParcelsParcelidInvoices } from '@/shared/api/generated';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

/**
 * Comprovantes de pagamento de uma parcela.
 *
 * Alimenta a tabela somente leitura da tela de edição, com as mesmas colunas da
 * tela legada. É uma leitura independente do formulário: falhar aqui não impede
 * editar a parcela, e por isso o estado de erro fica na própria seção.
 *
 * Reemitir, cancelar e enviar comprovante são operações próprias de
 * `individualPaymentInvoicesController` e **não** fazem parte do Salvar da tela.
 *
 * @param parcelId - ID da parcela; `null` desabilita a query, evitando uma
 *   requisição com `NaN` na URL.
 *
 * @example
 * ```tsx
 * const { invoices, isLoading, isError, refetch } = useParcelInvoices(42);
 * ```
 */
export function useParcelInvoices(parcelId: number | null) {
  const { data, isLoading, isError, refetch } = useGetApiV1IndividualpaymentParcelsParcelidInvoices(
    parcelId ?? 0,
    undefined,
    { query: { enabled: parcelId !== null } },
  );

  // O Kubb tipa o corpo como array, mas o backend já foi observado envelopando
  // respostas desta API em `{ data: [...] }` — sem normalizar, o `.map` da tabela
  // quebra em runtime.
  const invoices: readonly IndividualPaymentInvoiceViewModel[] =
    unwrapPagedResponse<IndividualPaymentInvoiceViewModel>(data);

  return {
    invoices,
    isLoading: parcelId !== null && isLoading,
    isError,
    refetch: () => void refetch(),
  } as const;
}
